import type { Immutable } from "immer";
import React from "react";
import { ProductReviewsSSR } from "tsi-common-react/src/apps/reviews/ProductReviewsSSR/index";

import type {
    FullReviewsModule as FullReviewsModuleValue,
    templates,
} from "@reactivated";

type IProps = Immutable<{
    value: FullReviewsModuleValue;
    reviews: templates.ProductReviewsTemplate | null;
    page_num_reviews: number;
}>;

/**
 * Standalone (Phase 1) port of the legacy
 * ``cms/components/pdp-reviews-full.html`` (#33464):
 *
 * - The ``.pdp-section`` wrapper ALWAYS renders (the legacy ``{% if
 *   page.reviews_brand_id %}`` gate sat INSIDE it).
 * - ``props.reviews`` is the page-derived SSR bundle from the
 *   ``get_full_reviews_props`` templatetag; it is ``null`` when the page gate
 *   fails, so nothing inside the wrapper renders (legacy parity). The extra
 *   ``reviews_brand_id`` check mirrors sealy's / toppers' consolidated
 *   ``reviews_full`` component-map entry.
 * - ``ProductReviewsSSR`` gets ``starHasStroke={true}`` +
 *   ``isCollapsedView={true}`` and NO ``brand`` -- matching THIS site's legacy
 *   ``ProductReviewsTemplate.tsx`` re-export (the island the legacy ``{%
 *   react_product_reviews %}`` tag mounted). NOTE: this differs from sealy's
 *   FullReviewsModule (which passes ``brand="Sealy"``) because tempurpedic's
 *   ``ProductReviewsTemplate`` re-export passes no brand.
 * - The CTA gates on ``value.show_cta && page_num_reviews > 0`` -- the legacy
 *   ``page.num_reviews`` page-level simple sum, NOT the bundle-deduped
 *   ``reviews.num_reviews``.
 * - The legacy template renders NO UGC markup (``show_ugc`` / ``ugc`` are
 *   consumed only inside ``react_product_reviews``'s ``ugc_data``), so there is
 *   no separate UGC section here -- exact legacy parity.
 * - The CTA href uses the Link snippet's resolved ``url`` (mirroring sealy's /
 *   toppers' port): the legacy ``{% if value.cta.link_page %}{{
 *   value.cta.link_page.get_url }}{% else %}{{ value.cta }}{% endif %}``
 *   rendered ``str(Link)`` (a debug-ish "Link[...]: url" string) for non-page
 *   links, which was a latent template bug; ``cta.url`` resolves every link
 *   type correctly.
 */
export const FullReviewsModule = (props: IProps): React.JSX.Element => {
    const reviews = props.reviews;
    return (
        <div className="pdp-section">
            {reviews && reviews.reviews_brand_id && (
                <>
                    <div id="reviews">
                        <ProductReviewsSSR
                            {...reviews}
                            starHasStroke={true}
                            isCollapsedView={true}
                        />
                    </div>
                    {props.value.show_cta && props.page_num_reviews > 0 && (
                        <div className="pdp-reviews-full__cta">
                            <a
                                className="button button--ghost pdp-reviews-full__cta-link"
                                href={props.value.cta?.url ?? ""}
                            >
                                {props.value.cta_text}
                            </a>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
